'use client';

import Image from 'next/image';
import { useContext, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { AmplitudeContext } from '@/contexts/amplitude';
type HeroProps = {
  id?: string;
  mobileImage: { src: string; alt: string };
  tabletImage: { src: string; alt: string };
  image: { src: string; alt: string };
  title: string;
  description: string;
  button: JSX.Element;
  analyticsSectionReference?: string;
  isCard?: boolean;
  isCardMd?: boolean;
  classNameImage?: string;
  classNameCard?: string;
};
const Hero: React.FC<HeroProps> = ({
  id,
  mobileImage,
  tabletImage,
  image,
  title,
  description,
  button,
  analyticsSectionReference,
  isCard = false,
  isCardMd = false,
  classNameImage,
  classNameCard,
}) => {
  const { analytics } = useContext(AmplitudeContext);

  const [ref, inView] = useInView({
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView && analyticsSectionReference) {
      analytics?.track({
        event_type: 'section viewed',
        event_properties: {
          name: 'section viewed',
          description:
            'Evento disparado quando o usuário visualiza uma sessão especifica da pagina',
          section_reference: analyticsSectionReference,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics, inView]);

  return (
    <section
      ref={ref}
      className={`flex w-full ${isCard ? 'bg-display-50' : 'bg-display-0'} ${
        isCardMd ? 'md:bg-display-0' : 'md:bg-display-50'
      }`}
      id={id}
    >
      <div
        className={`flex flex-col md:flex-row gap-16 md:gap-24 lg:gap-40 justify-between lg:justify-between items-center md:pl-24 m-auto w-full max-w-[455px] ${
          classNameCard ? classNameCard : 'md:max-w-[1280px]  md:justify-center'
        }`}
      >
        <div className="md:hidden overflow-hidden relative w-full h-[300px] rounded-br-[42px]">
          <Image
            src={mobileImage.src}
            alt={mobileImage.alt}
            quality={80}
            className="object-cover rounded-br-[42px]"
            fill
          />
        </div>
        <div className="flex flex-col md:flex-1 lg:flex-initial gap-16 items-center md:items-start px-24 md:px-0 pb-40 md:pb-0 max-w-[455px] lg:max-w-[565px] text-center md:text-left">
          <h1
            className="font-display text-[36px] lg:text-[48px] font-black leading-[44px] lg:leading-[56px] text-center md:text-left"
            dangerouslySetInnerHTML={{
              __html: title,
            }}
          />
          <p
            className={`md:order-1 w-full text-lg leading-[26px] text-center md:text-left ${
              isCard
                ? 'lg:text-xl lg:leading-[28px]'
                : 'lg:leading-6 lg:text-2xl order-3'
            }`}
          >
            {description}
          </p>

          {button}
        </div>
        <div
          className={`relative hidden md:flex lg:hidden overflow-hidden flex-1 lg:flex-initial md:min-w-[372px] lg:rounded-br-10xl ${
            classNameImage ? classNameImage : ''
          } ${!isCard && 'md:min-h-[576px]'}`}
        >
          <Image
            src={tabletImage.src}
            alt={tabletImage.alt}
            width={500}
            height={576}
            quality={80}
            className=" object-cover rounded-br-6xl lg:rounded-br-10xl"
            layout="responsive"
          />
        </div>
        <div className={classNameImage}>
          <div className="hidden lg:flex overflow-hidden relative flex-1 lg:flex-initial lg:min-w-[500px] lg:min-h-[576px] lg:rounded-br-10xl">
            <Image
              src={image.src}
              alt={image.alt}
              width={675}
              height={576}
              quality={80}
              className=" object-cover rounded-br-6xl lg:rounded-br-10xl"
              layout="responsive"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
